import { Dtos, SuretyAccountId, SuretyBondId, SuretyQuoteId } from "@inrev/common";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { followersQueryKey } from "../followers/followers.keys";
import { SuretyEntityIdMap } from "../types";
import { commentsQueryKey, createCommentMutationKey } from "./comments.keys";

const SEND_TIMEOUT_MS = 30_000;

const accountCommentsRoot = (admin: boolean) => `/v2/${admin ? "admin/" : ""}surety/accounts`;

const useCommentList = <T extends keyof SuretyEntityIdMap>(
	url: string,
	type: T,
	id: SuretyEntityIdMap[T],
) => {
	const { get, post } = useRequest();
	const queryClient = useQueryClient();
	const queryKey = commentsQueryKey(type, id);
	const mutationKey = createCommentMutationKey(type, id);

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => await get<Dtos.Comment.Get.Response[]>(url),
	});

	const mutation = useMutation({
		mutationKey,
		// The composer stays locked until the send settles and keeps the draft if it fails, so settle
		// fast: don't pause offline, don't retry (a retried POST can post twice), and time out a hang.
		networkMode: "always",
		retry: false,
		mutationFn: async (message: string) =>
			await post<Dtos.Comment.Get.Response>(
				url,
				{ message },
				"json",
				AbortSignal.timeout(SEND_TIMEOUT_MS),
			),
		onSuccess: (comment) => {
			queryClient.cancelQueries({ queryKey });
			queryClient.setQueryData(queryKey, (oldData: Dtos.Comment.Get.Response[] | undefined) =>
				oldData ? [...oldData, comment] : [comment],
			);
			queryClient.invalidateQueries({ queryKey: followersQueryKey(type, id) });
		},
	});

	return {
		comments: data,
		commentsLoading: isLoading,
		// Rejects on failure, so callers must catch it. The query client already shows the error.
		createComment: mutation.mutateAsync,
		createCommentLoading: useIsMutating({ mutationKey }) > 0,
	};
};

export const useAccountComments = (accountId: SuretyAccountId, admin = false) =>
	useCommentList(`${accountCommentsRoot(admin)}/${accountId}/comments`, "accounts", accountId);

export const useQuoteComments = (
	accountId: SuretyAccountId,
	quoteId: SuretyQuoteId,
	admin = false,
) =>
	useCommentList(
		`${accountCommentsRoot(admin)}/${accountId}/quotes/${quoteId}/comments`,
		"quotes",
		quoteId,
	);

export const useBondComments = (accountId: SuretyAccountId, bondId: SuretyBondId, admin = false) =>
	useCommentList(
		`${accountCommentsRoot(admin)}/${accountId}/bonds/${bondId}/comments`,
		"bonds",
		bondId,
	);
